import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Loader2, ChevronLeft, ChevronRight, Boxes } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOsMe, OsStatusBadge, OsDenied, OS_STATUS_FLOW, OS_STATUS_LABEL } from "./osShared";
import { DueBadge } from "./caseWorkflow";

const LIMIT = 20;

export default function OsCases() {
  const { me, denied } = useOsMe();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [creating, setCreating] = useState(false);
  const [propNames, setPropNames] = useState({});
  const [userEmails, setUserEmails] = useState({});

  useEffect(() => {
    api.get("/os/properties?limit=200").then((r) =>
      setPropNames(Object.fromEntries(r.data.items.map((p) => [p.id, p.designation])))
    ).catch(() => {});
    api.get("/os/users?limit=200").then((r) =>
      setUserEmails(Object.fromEntries(r.data.items.map((u) => [u.id, u.email])))
    ).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams({ offset, limit: LIMIT });
    if (status) p.set("status", status);
    api.get(`/os/cases?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [offset, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (showNew) api.get("/os/properties?limit=200").then((r) => setProperties(r.data.items)).catch(() => {});
  }, [showNew]);

  if (denied) return <OsDenied />;
  const canCreate = me && ["ADMIN", "HANDLAGGARE", "MEDBORGARE"].includes(me.role);

  const createCase = async () => {
    setCreating(true);
    try {
      const { data: c } = await api.post("/os/cases", { propertyId });
      toast.success(`Ärende ${c.caseNumber} skapat`);
      navigate(`/os/cases/${c.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa ärende");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in-up" data-testid="os-cases-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center rounded-sm"><Boxes className="w-5 h-5 stroke-[1.5]" /></div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Bygglovs-OS · Ärenden</h1>
            <p className="text-xs text-zinc-500">{me?.tenant?.name} · roll: {me?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/os/map" data-testid="os-nav-map" className="text-sm text-blue-700 font-medium hover:underline">Karta</Link>
          <Link to="/os/properties" data-testid="os-nav-properties" className="text-sm text-blue-700 font-medium hover:underline">Fastigheter</Link>
          {canCreate && (
            <button onClick={() => setShowNew(true)} data-testid="os-new-case"
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium">
              <Plus className="w-4 h-4" /> Nytt ärende
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-1.5">
        <button onClick={() => { setStatus(""); setOffset(0); }} data-testid="os-filter-all"
          className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${!status ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>Alla</button>
        {OS_STATUS_FLOW.map((s) => (
          <button key={s} onClick={() => { setStatus(s); setOffset(0); }} data-testid={`os-filter-${s}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${status === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
            {OS_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 bg-zinc-50 border border-zinc-200 rounded-sm p-10 text-center text-sm text-zinc-500" data-testid="os-cases-empty">Inga ärenden.</div>
      ) : (
        <div className="mt-4 bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="os-cases-list">
          {data.items.map((c) => (
            <Link key={c.id} to={`/os/cases/${c.id}`} data-testid={`os-case-row-${c.caseNumber}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition">
              <div>
                <div className="text-xs font-mono text-zinc-500">{c.caseNumber}</div>
                <div className="text-sm text-zinc-700">Fastighet: {propNames[c.propertyId] || c.propertyId}</div>
                {c.assigneeId && <div className="text-[11px] text-zinc-400">Handläggare: {userEmails[c.assigneeId] || c.assigneeId}</div>}
              </div>
              <div className="flex items-center gap-3">
                <DueBadge dueDate={c.dueDate} status={c.status} />
                <span className="text-xs text-zinc-400">{(c.createdAt || "").slice(0, 10)}</span>
                <OsStatusBadge status={c.status} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-between text-sm text-zinc-600">
          <span>{offset + 1}–{Math.min(offset + LIMIT, data.total)} av {data.total}</span>
          <div className="flex gap-2">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="os-page-prev"
              className="p-1.5 border border-zinc-300 rounded-sm disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <button disabled={offset + LIMIT >= data.total} onClick={() => setOffset(offset + LIMIT)} data-testid="os-page-next"
              className="p-1.5 border border-zinc-300 rounded-sm disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-sm" data-testid="os-new-case-modal">
          <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-6">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Nytt OS-ärende</h3>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Fastighet</label>
            <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} data-testid="os-case-property-select"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700">
              <option value="">Välj fastighet…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.designation}</option>)}
            </select>
            {properties.length === 0 && <p className="mt-2 text-xs text-zinc-500">Inga fastigheter ännu – skapa en under <Link to="/os/properties" className="text-blue-700 underline">Fastigheter</Link>.</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm">Avbryt</button>
              <button disabled={!propertyId || creating} onClick={createCase} data-testid="os-case-create-submit"
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Skapa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
